// src/hooks/useCustomersPG.js
import { useState, useEffect, useCallback, useMemo } from 'react';
import CustomerAPI from '../services/api/customerAPI';
import { Customer } from '../models';
import { cacheManager } from '../services/cacheManager';

export function useCustomersPG() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

  // Clear error
  const clearError = useCallback(() => setError(null), []);

  const CUSTOMERS_STORAGE_KEY = 'salon_customers_cache';

  const saveCustomersToStorage = (customers) => {
    try {
      const cacheData = {
        customers,
        timestamp: Date.now()
      };
      localStorage.setItem(CUSTOMERS_STORAGE_KEY, JSON.stringify(cacheData));
    } catch (error) {
      console.warn('Failed to save customers to localStorage:', error);
    }
  };

  const loadCustomersFromStorage = async () => {
    try {
      const cached = localStorage.getItem(CUSTOMERS_STORAGE_KEY);
      if (!cached) return null;

      const { customers, timestamp } = JSON.parse(cached);

      // Check Firestore invalidation
      const isValid = await cacheManager.isCacheValid('customers', timestamp);
      if (!isValid) {
        console.log('Customers cache invalidated by Firestore');
        localStorage.removeItem(CUSTOMERS_STORAGE_KEY);
        return null;
      }

      return customers;
    } catch (error) {
      console.warn('Failed to load customers from localStorage:', error);
      return null;
    }
  };

  const clearCustomersStorage = () => {
    localStorage.removeItem(CUSTOMERS_STORAGE_KEY);
  };

  // Load all customers (replaces Firebase autoLoad)
  const loadAllCustomers = useCallback(async (forceReload = false, options = {}) => {
    if (loading) return;

    // Check local storage first (unless forced reload)
    if (!forceReload) {
      const cachedCustomers = await loadCustomersFromStorage();
      if (cachedCustomers && cachedCustomers.length > 0) {
        const customerInstances = cachedCustomers.map(customer => Customer.fromJSON(customer));
        setCustomers(customerInstances);
        return customerInstances;
      }
    }

    setLoading(true);
    setError(null);

    try {
      console.log('Hook: Loading customers from API', { options });

      const data = await CustomerAPI.getAll(options);

      const customerInstances = data.map(customer => Customer.fromJSON({
        ...customer,
        // Ensure consistent field mapping
        id: customer.id,
        full_name: customer.full_name,
        phone: customer.phone,
        email: customer.email,
        birth_date: customer.birth_date,
        address: customer.address,
        notes: customer.notes,
        skin_type: customer.skin_type,
        allergies: customer.allergies,
        preferred_contact: customer.preferred_contact,
        created_at: customer.created_at,
        updated_at: customer.updated_at
      }));

      console.log('Hook: Successfully loaded customers from API', {
        count: customerInstances.length,
        options
      });

      setCustomers(customerInstances);
      saveCustomersToStorage(data);
      return customerInstances;

    } catch (err) {
      console.error('Hook: Error loading customers from API:', {
        error: err.message,
        stack: err.stack,
        options
      });
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [loading]);

  // Load customers only once on mount, with local storage
  useEffect(() => {
    const initializeCustomers = async () => {
      if (hasLoadedOnce) return;

      setHasLoadedOnce(true);
      await loadAllCustomers(false); // false = allow cache usage
    };

    initializeCustomers();
  }, []);

  // Convert customers to Customer instances for compatibility
  const customerInstances = useMemo(() => {
    return customers.map(customer => {
      if (customer instanceof Customer) {
        return customer;
      }
      return Customer.fromJSON(customer);
    });
  }, [customers]);

  // CRUD Operations - maintaining same interface as Firebase hook
  const addCustomer = useCallback(async (customerData) => {
    setLoading(true);
    setError(null);

    try {
      console.log('Hook: Adding new customer', {
        fullName: customerData?.full_name,
        phone: customerData?.phone,
        email: customerData?.email
      });

      // Client-side validation using Customer model
      const validation = Customer.validate(customerData);
      if (!validation.isValid) {
        throw new Error(`Validation failed: ${validation.errors.join(', ')}`);
      }

      // Check for duplicate phone numbers in local state first (quick check)
      if (customerData.phone) {
        const cleanPhone = customerData.phone.replace(/\D/g, '');
        const duplicateInLocal = customers.find(c =>
          c.phone && c.phone.replace(/\D/g, '') === cleanPhone
        );

        if (duplicateInLocal) {
          throw new Error(`A customer with phone number ${cleanPhone} already exists: ${duplicateInLocal.full_name}`);
        }
      }

      // Check for duplicate email in local state first (quick check)
      if (customerData.email && customerData.email.trim()) {
        const duplicateEmailInLocal = customers.find(c =>
          c.email && c.email.toLowerCase() === customerData.email.trim().toLowerCase()
        );

        if (duplicateEmailInLocal) {
          throw new Error(`A customer with email ${customerData.email.trim()} already exists: ${duplicateEmailInLocal.full_name}`);
        }
      }

      // Create new customer via API
      console.log('Hook: Creating customer via API');
      const newCustomer = await CustomerAPI.create(customerData);

      console.log('Hook: Customer created successfully via API', {
        customerId: newCustomer.id,
        fullName: newCustomer.full_name,
        phone: newCustomer.phone
      });

      // Convert to Customer instance
      const customerInstance = Customer.fromJSON(newCustomer);

      // Update local state - add to beginning of array for immediate visibility
      setCustomers(prev => {
        const updated = [customerInstance, ...prev];
        // Invalidate cache and save new state
        saveCustomersToStorage(updated);
        return updated;
      });

      // Reset the hasLoadedOnce flag to ensure fresh data on next load
      setHasLoadedOnce(false);

      await cacheManager.invalidateCache('customers');
      console.log('Hook: Successfully added customer to local state', {
        customerId: customerInstance.id,
        totalCustomers: customers.length + 1
      });

      return customerInstance;

    } catch (err) {
      console.error('Hook: Error adding customer:', {
        customerData: {
          full_name: customerData?.full_name,
          phone: customerData?.phone,
          email: customerData?.email
        },
        error: err.message,
        stack: err.stack
      });

      const errorMessage = `Failed to add customer: ${err.message}`;
      setError(errorMessage);
      throw new Error(errorMessage);

    } finally {
      setLoading(false);
    }
  }, [customers]);

  // Update an existing customer - Extra careful implementation with rollback capability
  const updateCustomer = useCallback(async (customerId, updateData) => {
    setLoading(true);
    setError(null);

    // Store original state for potential rollback
    const originalCustomers = [...customers];

    try {
      console.log('Hook: Updating customer', {
        customerId,
        updateFields: Object.keys(updateData || {}),
        hasPhone: !!(updateData?.phone),
        hasEmail: !!(updateData?.email)
      });

      // Critical validation
      if (!customerId) {
        throw new Error('Customer ID is required for update');
      }

      if (!updateData || typeof updateData !== 'object' || Array.isArray(updateData)) {
        throw new Error('Update data must be a valid object');
      }

      if (Object.keys(updateData).length === 0) {
        throw new Error('At least one field must be provided for update');
      }

      // Find the existing customer in local state
      const existingCustomerIndex = customers.findIndex(c => c.id === customerId);
      if (existingCustomerIndex === -1) {
        throw new Error(`Customer with ID ${customerId} not found in local state`);
      }

      const existingCustomer = customers[existingCustomerIndex];
      console.log('Hook: Found existing customer', {
        customerId: existingCustomer.id,
        currentName: existingCustomer.full_name,
        currentPhone: existingCustomer.phone
      });

      // Validate the updated data using Customer model
      const mergedData = { ...existingCustomer, ...updateData };
      const validation = Customer.validate(mergedData);
      if (!validation.isValid) {
        throw new Error(`Validation failed: ${validation.errors.join(', ')}`);
      }

      // Critical: Check for duplicates in local state first (quick validation)
      if (updateData.hasOwnProperty('phone') && updateData.phone && updateData.phone.trim()) {
        const cleanPhone = updateData.phone.replace(/\D/g, '');
        const existingPhone = existingCustomer.phone ? existingCustomer.phone.replace(/\D/g, '') : '';

        // Only check if phone is actually changing
        if (cleanPhone !== existingPhone) {
          const duplicatePhone = customers.find(c =>
            c.id !== customerId &&
            c.phone &&
            c.phone.replace(/\D/g, '') === cleanPhone
          );

          if (duplicatePhone) {
            throw new Error(`Phone number ${cleanPhone} is already used by another customer: ${duplicatePhone.full_name}`);
          }
        }
      }

      if (updateData.hasOwnProperty('email') && updateData.email && updateData.email.trim()) {
        const cleanEmail = updateData.email.trim().toLowerCase();
        const existingEmail = existingCustomer.email ? existingCustomer.email.toLowerCase() : '';

        // Only check if email is actually changing
        if (cleanEmail !== existingEmail) {
          const duplicateEmail = customers.find(c =>
            c.id !== customerId &&
            c.email &&
            c.email.toLowerCase() === cleanEmail
          );

          if (duplicateEmail) {
            throw new Error(`Email ${cleanEmail} is already used by another customer: ${duplicateEmail.full_name}`);
          }
        }
      }

      // Optimistic update: Update local state immediately for better UX
      const optimisticUpdatedCustomer = Customer.fromJSON({ ...existingCustomer, ...updateData });
      const optimisticUpdatedCustomers = [...customers];
      optimisticUpdatedCustomers[existingCustomerIndex] = optimisticUpdatedCustomer;

      setCustomers(optimisticUpdatedCustomers);
      console.log('Hook: Applied optimistic update to local state');

      try {
        // Make the API call
        console.log('Hook: Updating customer via API');
        const updatedCustomer = await CustomerAPI.update(customerId, updateData);

        console.log('Hook: Customer updated successfully via API', {
          customerId: updatedCustomer.id,
          fullName: updatedCustomer.full_name,
          phone: updatedCustomer.phone,
          updatedAt: updatedCustomer.updated_at
        });

        // Convert API response to Customer instance
        const customerInstance = Customer.fromJSON(updatedCustomer);

        // Update local state with server response (replaces optimistic update)
        // Use functional update to avoid stale closure from before the optimistic update
        let finalCustomersForCache;
        setCustomers(prev => {
          const updated = [...prev];
          const idx = updated.findIndex(c => c.id === customerId);
          if (idx !== -1) updated[idx] = customerInstance;
          finalCustomersForCache = updated;
          return updated;
        });

        // Update cache with the final data
        saveCustomersToStorage(finalCustomersForCache);

        // Reset the hasLoadedOnce flag to ensure fresh data on next load
        setHasLoadedOnce(false);
        await cacheManager.invalidateCache('customers');

        // CRITICAL: If customer name was updated, clear appointments cache
        // This ensures appointment lists refresh with the new customer name
        if (updateData.hasOwnProperty('full_name')) {
          console.log('Hook: Customer name was updated - clearing appointments cache', {
            customerId,
            oldName: existingCustomer.full_name,
            newName: updateData.full_name
          });

          // Clear appointments cache from localStorage
          localStorage.removeItem('salon_appointments_cache');

          // Invalidate appointments cache in Firestore
          await cacheManager.invalidateCache('appointments');

          console.log('Hook: Successfully cleared appointments cache after customer name update');
        }

        console.log('Hook: Successfully updated customer in local state and cache');
        return customerInstance;

      } catch (apiError) {
        // CRITICAL: Rollback optimistic update on API failure
        console.error('Hook: API call failed, rolling back optimistic update', {
          customerId,
          error: apiError.message
        });

        setCustomers(originalCustomers);
        console.log('Hook: Rolled back to original state');

        // Re-throw the API error
        throw apiError;
      }

    } catch (err) {
      console.error('Hook: Error updating customer', {
        customerId,
        updateFields: Object.keys(updateData || {}),
        error: err.message,
        stack: err.stack
      });

      // Ensure state is restored to original if not already done
      if (customers !== originalCustomers) {
        setCustomers(originalCustomers);
        console.log('Hook: Restored original state after error');
      }

      const errorMessage = `Failed to update customer: ${err.message}`;
      setError(errorMessage);
      throw new Error(errorMessage);

    } finally {
      setLoading(false);
    }
  }, [customers]);

  // Delete a customer - DESTRUCTIVE OPERATION - Ultra-safe implementation with rollback
  const deleteCustomer = useCallback(async (customerId, options = {}) => {
    setLoading(true);
    setError(null);

    // Store original state for potential rollback
    const originalCustomers = [...customers];

    try {
      console.warn('Hook: CRITICAL - Customer deletion requested', {
        customerId,
        hasConfirmation: !!options.confirmDeletion,
        timestamp: new Date().toISOString()
      });

      // CRITICAL validation
      if (!customerId) {
        throw new Error('Customer ID is required for deletion');
      }

      if (typeof customerId !== 'string') {
        throw new Error('Customer ID must be a string');
      }

      // CRITICAL: Require explicit confirmation to prevent accidental deletions
      if (!options.confirmDeletion) {
        throw new Error(
          'Deletion confirmation required. This is a destructive operation that cannot be undone. ' +
          'Set confirmDeletion: true in options to proceed.'
        );
      }

      if (options.confirmDeletion !== true) {
        throw new Error('Deletion confirmation must be exactly true');
      }

      // Find the customer in local state
      const customerIndex = customers.findIndex(c => c.id === customerId);
      if (customerIndex === -1) {
        throw new Error(`Customer with ID ${customerId} not found in local state`);
      }

      const customerToDelete = customers[customerIndex];

      // CRITICAL SAFETY CHECK: Log what we're about to delete
      console.warn('Hook: About to delete customer with full data', {
        customerId: customerToDelete.id,
        fullName: customerToDelete.full_name,
        phone: customerToDelete.phone,
        email: customerToDelete.email,
        hasNotes: !!customerToDelete.notes,
        hasAllergies: !!customerToDelete.allergies,
        accountAge: customerToDelete.created_at
      });

      // ADDITIONAL SAFETY: Check if customer has important data that might be lost
      const hasImportantData = !!(
        customerToDelete.notes ||
        customerToDelete.allergies ||
        customerToDelete.skin_type ||
        customerToDelete.address
      );

      if (hasImportantData && !options.forceDeleteWithData) {
        throw new Error(
          `Customer "${customerToDelete.full_name}" has important data (notes, allergies, skin type, or address). ` +
          'If you really want to delete this customer and lose this data permanently, ' +
          'set forceDeleteWithData: true in options along with confirmDeletion: true.'
        );
      }

      // Optimistic update: Remove from local state immediately
      const optimisticUpdatedCustomers = customers.filter(c => c.id !== customerId);
      setCustomers(optimisticUpdatedCustomers);
      console.warn('Hook: Applied optimistic deletion to local state');

      try {
        // Make the API call
        console.warn('Hook: Proceeding with customer deletion via API');
        const deletionResult = await CustomerAPI.delete(customerId, {
          confirmDeletion: true
        });

        await cacheManager.invalidateCache('customers');

        console.warn('Hook: Customer deleted successfully via API', {
          deletedCustomerId: deletionResult.deletedCustomerId,
          customerName: deletionResult.deletedCustomer?.full_name,
          deletionTimestamp: deletionResult.deletionTimestamp
        });

        clearCustomersStorage();
        const freshCustomers = await loadAllCustomers(true);

        // Force re-render by explicitly setting state
        setCustomers([...freshCustomers]);

        // Reset the hasLoadedOnce flag to ensure fresh data on next load
        setHasLoadedOnce(false);

        console.warn('Hook: Successfully removed customer from local state and cache');

        return {
          success: true,
          deletedCustomerId: deletionResult.deletedCustomerId,
          deletedCustomer: deletionResult.deletedCustomer,
          message: deletionResult.message
        };

      } catch (apiError) {
        // CRITICAL: Rollback optimistic deletion on API failure
        console.error('Hook: API deletion failed, rolling back optimistic deletion', {
          customerId,
          error: apiError.message
        });

        setCustomers(originalCustomers);
        console.warn('Hook: Rolled back to original state - customer restoration completed');

        // Re-throw the API error
        throw apiError;
      }

    } catch (err) {
      console.error('Hook: Error deleting customer', {
        customerId,
        error: err.message,
        stack: err.stack,
        timestamp: new Date().toISOString()
      });

      // Ensure state is restored to original if not already done
      if (customers !== originalCustomers) {
        setCustomers(originalCustomers);
        console.warn('Hook: Restored original state after deletion error');
      }

      const errorMessage = `Failed to delete customer: ${err.message}`;
      setError(errorMessage);
      throw new Error(errorMessage);

    } finally {
      setLoading(false);
    }
  }, [customers]);

  // Get a customer by ID - Updated to use CustomerAPI
  const getCustomer = useCallback(async (customerId) => {
    setError(null);

    try {
      console.log('Hook: Getting customer by ID', { customerId });

      // Try to find in local state first for performance
      const localCustomer = customers.find(c => c.id === customerId);
      if (localCustomer) {
        console.log('Hook: Found customer in local state', { customerId });
        return localCustomer;
      }

      console.log('Hook: Customer not found locally, fetching from API', { customerId });

      // Fetch from Firebase Functions API if not found locally
      const customer = await CustomerAPI.getById(customerId);

      if (!customer) {
        console.log('Hook: Customer not found in API', { customerId });
        return null;
      }

      // Convert to Customer instance
      const customerInstance = Customer.fromJSON(customer);

      // Add to local state cache for future reference
      setCustomers(prev => {
        // Only add if not already present
        const exists = prev.some(c => c.id === customerId);
        if (!exists) {
          console.log('Hook: Adding fetched customer to local state', { customerId });
          return [...prev, customerInstance];
        }
        return prev;
      });

      console.log('Hook: Successfully retrieved customer by ID from API', {
        customerId: customerInstance.id,
        fullName: customerInstance.full_name
      });

      return customerInstance;

    } catch (err) {
      console.error('Hook: Error getting customer by ID:', {
        customerId,
        error: err.message,
        stack: err.stack
      });
      setError(err.message);
      throw err;
    }
  }, [customers]);

  // Handle customer submission (add or edit) - maintains same interface
  const handleCustomerSubmit = useCallback(async (customerDataOrId, modeOrData, mode) => {
    try {
      if (mode === 'edit') {
        // customerDataOrId is the ID, modeOrData is the update data
        await updateCustomer(customerDataOrId, modeOrData);
        console.log('Customer updated successfully');
        return { success: true, mode: 'edit' };
      } else {
        // customerDataOrId is the new customer data
        await addCustomer(customerDataOrId);
        console.log('Customer added successfully');
        return { success: true, mode: 'add' };
      }
    } catch (error) {
      console.error('Error saving customer:', error);
      throw error; // Re-throw so the calling component can handle it
    }
  }, [addCustomer, updateCustomer]);

  // Search customers using PostgreSQL full-text search
  const searchCustomers = useCallback(async (searchTerm, useLocal = true) => {
    if (!useLocal) {
      throw new Error("searchCustomer not locally is not supported.")
    }
    if (!searchTerm || !searchTerm.trim()) {
      return customers;
    }

    // Option to search locally (fast) or via API (more comprehensive)
    if (useLocal) {
      const term = searchTerm.toLowerCase().trim();
      return customers.filter(customer =>
        customer.full_name?.toLowerCase().includes(term) ||
        customer.phone?.includes(term) ||
        customer.email?.toLowerCase().includes(term) ||
        customer.notes?.toLowerCase().includes(term)
      );
    }

    try {
      // TODO: implement
    } catch (err) {

    }
  }, [customers]);

  /**
 * Get customer by phone number
 * @param {string} phone - Phone number (various formats accepted)
 * @returns {Promise<Object|null>} Customer instance or null if not found
 */
  const getCustomerByPhone = useCallback(async (phone) => {
    setError(null);

    try {
      console.log('Hook: Getting customer by phone', {
        phone: phone ? phone.substring(0, 3) + '***' : 'undefined'
      });

      // Validate phone parameter
      if (!phone) {
        throw new Error('Phone number is required');
      }

      if (typeof phone !== 'string') {
        throw new Error('Phone number must be a string');
      }

      // Try to find in local state first for performance
      const cleanPhone = phone.replace(/\D/g, '');
      const normalizedPhone = cleanPhone.startsWith('972') ?
        '0' + cleanPhone.substring(3) : cleanPhone;

      const localCustomer = customers.find(c => {
        if (!c.phone) return false;
        const localCleanPhone = c.phone.replace(/\D/g, '');
        const localNormalizedPhone = localCleanPhone.startsWith('972') ?
          '0' + localCleanPhone.substring(3) : localCleanPhone;
        return localNormalizedPhone === normalizedPhone || localCleanPhone === cleanPhone;
      });

      if (localCustomer) {
        console.log('Hook: Found customer by phone in local state', {
          customerId: localCustomer.id,
          phone: localCustomer.phone ? localCustomer.phone.substring(0, 3) + '***' : 'undefined'
        });
        return localCustomer;
      }

      console.log('Hook: Customer not found locally by phone, fetching from API', {
        phone: phone.substring(0, 3) + '***'
      });

      // Fetch from Firebase Functions API if not found locally
      const customer = await CustomerAPI.getByPhone(phone);

      if (!customer) {
        console.log('Hook: Customer not found by phone in API', {
          phone: phone.substring(0, 3) + '***'
        });
        return null;
      }

      // Convert to Customer instance
      const customerInstance = Customer.fromJSON(customer);

      // Add to local state cache for future reference (don't duplicate)
      setCustomers(prev => {
        const exists = prev.find(c => c.id === customerInstance.id);
        if (!exists) {
          console.log('Hook: Adding customer from phone lookup to local cache', {
            customerId: customerInstance.id,
            fullName: customerInstance.full_name
          });
          return [...prev, customerInstance];
        }
        return prev;
      });

      console.log('Hook: Successfully retrieved customer by phone', {
        customerId: customerInstance.id,
        fullName: customerInstance.full_name,
        phone: customerInstance.phone ? customerInstance.phone.substring(0, 3) + '***' : 'undefined',
        email: customerInstance.email
      });

      return customerInstance;

    } catch (err) {
      console.error('Hook: Error getting customer by phone:', {
        phone: phone ? phone.substring(0, 3) + '***' : 'undefined',
        error: err.message
      });
      setError(err.message);
      throw err;
    }
  }, [customers]);

  /**
 * Get customer's appointment history
 * SECURITY NOTE: This method does NOT access local customer cache to avoid
 * exposing other customers' data in customer-facing applications
 * @param {string} customerId - Customer ID (UUID)
 * @param {number} limit - Maximum number of appointments to return (default: 0 for no limit, max: 200)
 * @returns {Promise<Array>} Array of appointment instances
 */
  const getCustomerAppointments = useCallback(async (customerId, limit = 0) => {
    setError(null);

    try {
      console.log('Hook: Getting customer appointments from API', {
        customerId: customerId ? customerId.substring(0, 8) + '***' : 'undefined',
        limit: limit === 0 ? 'unlimited' : limit
      });

      // Validate customer ID parameter
      if (!customerId) {
        throw new Error('Customer ID is required');
      }

      if (typeof customerId !== 'string') {
        throw new Error('Customer ID must be a string');
      }

      // Validate UUID format
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
      if (!uuidRegex.test(customerId)) {
        throw new Error('Invalid customer ID format');
      }

      // Validate limit (0 means no limit)
      if (typeof limit !== 'number' || limit < 0 || limit > 200) {
        throw new Error('Limit must be a number between 0 and 200 (0 means no limit)');
      }

      // IMPORTANT: Direct API call - do NOT check local customers cache
      // This prevents exposing other customers' data in customer-facing apps
      console.log('Hook: Fetching customer appointments directly from API (bypassing local cache for security)');

      // Call CustomerAPI directly to get appointments
      const appointments = await CustomerAPI.getCustomerAppointments(customerId, limit);

      // Convert to Appointment instances (assuming Appointment model exists)
      const appointmentInstances = appointments.map(apt => {
        // Create appointment instance with proper data mapping
        return {
          id: apt.id,
          customer_id: apt.customer_id,
          service_id: apt.service_id,
          crew_member_id: apt.crew_member_id,
          date: apt.date,
          time: apt.time,
          date: apt.date,
          time: apt.time,
          duration: apt.duration,
          price: apt.price,
          status: apt.status,
          notes: apt.notes,
          customer_name: apt.customer_name,
          customer_phone: apt.customer_phone,
          service_name: apt.service_name,
          crew_member_name: apt.crew_member_name,
          created_at: apt.created_at,
          updated_at: apt.updated_at,
          // Additional details from joins
          service_description: apt.service_description,
          service_category: apt.service_category,
          crew_member_email: apt.crew_member_email,
          crew_member_role: apt.crew_member_role,
          crew_member_color: apt.crew_member_color,
          customer_full_name: apt.customer_full_name,
          customer_email: apt.customer_email,
          customer_skin_type: apt.customer_skin_type,
          customer_birth_date: apt.customer_birth_date
        };
      });

      console.log('Hook: Successfully retrieved customer appointments', {
        customerId: customerId.substring(0, 8) + '***',
        limit,
        count: appointmentInstances.length,
        customerName: appointmentInstances[0]?.customer_name || 'Unknown',
        hasUpcoming: appointmentInstances.some(apt => apt.date >= new Date().toISOString().split('T')[0]),
        statusBreakdown: appointmentInstances.reduce((acc, apt) => {
          acc[apt.status] = (acc[apt.status] || 0) + 1;
          return acc;
        }, {})
      });

      return appointmentInstances;

    } catch (err) {
      console.error('Hook: Error getting customer appointments:', {
        customerId: customerId ? customerId.substring(0, 8) + '***' : 'undefined',
        limit,
        error: err.message
      });
      setError(err.message);
      throw err;
    }
  }, []); // Empty dependency array - no dependency on customers cache


  const getCustomerStats = useCallback(async (customerId) => {
    setError(null);

    try {
      console.log('Hook: Getting customer statistics', { customerId });

      // Validate customer ID
      if (!customerId) {
        throw new Error('Customer ID is required');
      }

      if (typeof customerId !== 'string') {
        throw new Error('Customer ID must be a string');
      }

      // Try to find basic customer in local state first for quick validation
      const localCustomer = customers.find(c => c.id === customerId);
      if (!localCustomer) {
        console.log('Hook: Customer not found in local state for stats', { customerId });
        // Don't throw error here - let API handle it in case customer exists but not loaded locally
      }

      console.log('Hook: Fetching customer statistics from API', { customerId });

      // Fetch comprehensive stats from API (always fresh data)
      const customerStats = await CustomerAPI.getCustomerStats(customerId);

      if (!customerStats) {
        console.log('Hook: Customer statistics not found', { customerId });
        return null;
      }

      // Enhance stats with additional calculated fields for UI convenience
      const enhancedStats = {
        ...customerStats,

        // Add calculated percentages
        completion_percentage: customerStats.total_appointments > 0
          ? Math.round((customerStats.completed_appointments / customerStats.total_appointments) * 100)
          : 0,

        cancellation_percentage: customerStats.total_appointments > 0
          ? Math.round((customerStats.cancelled_appointments / customerStats.total_appointments) * 100)
          : 0,

        // Add activity status descriptions
        activity_status: customerStats.is_active ? 'Active' :
          customerStats.days_since_last_appointment <= 180 ? 'Inactive' : 'Dormant',

        // Add value tier description
        value_description: customerStats.is_high_value ? 'High Value' :
          customerStats.total_spent >= 500 ? 'Good Value' :
            customerStats.total_spent >= 200 ? 'Regular' : 'New',

        // Add appointment frequency description
        frequency_description: customerStats.is_frequent ? 'Frequent Visitor' :
          customerStats.total_appointments >= 5 ? 'Regular Visitor' :
            customerStats.total_appointments >= 2 ? 'Occasional Visitor' : 'New Customer',

        // Add tenure description
        tenure_description: customerStats.days_as_customer >= 365 ? 'Long-term Customer' :
          customerStats.days_as_customer >= 180 ? 'Established Customer' :
            customerStats.days_as_customer >= 90 ? 'Regular Customer' : 'New Customer',

        // Add recommendation score (0-100) based on multiple factors
        recommendation_score: calculateRecommendationScore(customerStats),

        // Add next action suggestions
        suggested_actions: generateSuggestedActions(customerStats),

        // Add comparison to averages (would need aggregate data - placeholder for now)
        above_average_spending: customerStats.avg_appointment_value > 120, // Placeholder threshold
        above_average_frequency: customerStats.total_appointments > 8 // Placeholder threshold
      };

      console.log('Hook: Successfully retrieved and enhanced customer statistics', {
        customerId: enhancedStats.id,
        fullName: enhancedStats.full_name,
        totalAppointments: enhancedStats.total_appointments,
        totalSpent: enhancedStats.total_spent,
        loyaltyTier: enhancedStats.loyalty_tier,
        activityStatus: enhancedStats.activity_status,
        recommendationScore: enhancedStats.recommendation_score,
        completionPercentage: enhancedStats.completion_percentage
      });

      return enhancedStats;

    } catch (err) {
      console.error('Hook: Error getting customer statistics:', {
        customerId,
        error: err.message,
        stack: err.stack
      });
      setError(err.message);
      throw err;
    }
  }, [customers]);

  // Helper function to calculate recommendation score
  const calculateRecommendationScore = (stats) => {
    let score = 50; // Base score

    // Loyalty tier impact (0-25 points)
    switch (stats.loyalty_tier) {
      case 'vip': score += 25; break;
      case 'loyal': score += 18; break;
      case 'regular': score += 10; break;
      case 'new': score += 5; break;
    }

    // Completion rate impact (0-20 points)
    const completionRate = parseFloat(stats.completion_rate) || 0;
    score += Math.min(20, completionRate * 0.2);

    // Activity impact (0-15 points)
    if (stats.is_active) score += 15;
    else if (stats.days_since_last_appointment <= 180) score += 8;

    // Frequency impact (0-15 points)
    if (stats.is_frequent) score += 15;
    else if (stats.total_appointments >= 5) score += 10;
    else if (stats.total_appointments >= 2) score += 5;

    // Value impact (0-15 points)
    if (stats.is_high_value) score += 15;
    else if (stats.total_spent >= 500) score += 10;
    else if (stats.total_spent >= 200) score += 5;

    // Tenure impact (0-10 points)
    if (stats.days_as_customer >= 365) score += 10;
    else if (stats.days_as_customer >= 180) score += 6;
    else if (stats.days_as_customer >= 90) score += 3;

    return Math.min(100, Math.max(0, Math.round(score)));
  };

  // Helper function to generate suggested actions
  const generateSuggestedActions = (stats) => {
    const actions = [];

    // Activity-based suggestions
    if (!stats.is_active && stats.days_since_last_appointment > 90) {
      actions.push({
        type: 'reactivation',
        priority: 'high',
        action: 'Send reactivation campaign',
        reason: `${stats.days_since_last_appointment} days since last visit`
      });
    }

    // Loyalty-based suggestions
    if (stats.loyalty_tier === 'vip' && stats.days_since_last_appointment > 30) {
      actions.push({
        type: 'retention',
        priority: 'high',
        action: 'Personal outreach',
        reason: 'VIP customer needs attention'
      });
    }

    // Completion rate suggestions
    const completionRate = parseFloat(stats.completion_rate) || 0;
    if (completionRate < 70 && stats.total_appointments >= 3) {
      actions.push({
        type: 'improvement',
        priority: 'medium',
        action: 'Follow up on cancellations',
        reason: `Low completion rate: ${completionRate}%`
      });
    }

    // Upselling suggestions
    if (stats.is_frequent && !stats.is_high_value) {
      actions.push({
        type: 'upsell',
        priority: 'medium',
        action: 'Offer premium services',
        reason: 'Frequent customer with upsell potential'
      });
    }

    // Birthday/anniversary suggestions
    if (stats.customer_since) {
      const daysSinceJoin = stats.days_as_customer;
      if (daysSinceJoin % 365 <= 7 && daysSinceJoin >= 365) {
        actions.push({
          type: 'celebration',
          priority: 'low',
          action: 'Send anniversary greeting',
          reason: 'Customer anniversary this week'
        });
      }
    }

    return actions;
  };

  // Compatibility methods for Firebase hook interface
  const rawCustomers = useMemo(() => customers, [customers]);

  return {
    // Data - maintaining same interface as Firebase hook
    customers: customerInstances,
    rawCustomers,
    loading,
    error,

    // Actions - same interface as Firebase hook
    addCustomer,
    updateCustomer,
    deleteCustomer,
    getCustomer,
    clearError,
    handleCustomerSubmit,

    // Queries - enhanced with PostgreSQL capabilities
    searchCustomers,
    getCustomerStats,
    getCustomerByPhone,
    getCustomerAppointments,

    // PostgreSQL-specific methods
    loadAllCustomers, // New method for explicit loading

    // Legacy compatibility
    customerInstances, // For backward compatibility
  };
}